import { LEFT_EAR_TYPE, RIGHT_EAR_TYPE } from "constants/products";

import { FORM_CONTEXT_LEAVES } from "../merge-tags-options/form-context-merge-tags.utils";
import type { MergeTagsReplaceHandler } from "./merge-tags-values";

/*
 * The `additionalMergeTagsValues` shape the form-context tags read (ENG-2277, D7).
 * Built per surface by the pure builders beside each Anvil payload builder.
 * A slot is absent when no device fills it: Left/Right by the device's ear,
 * Device1/Device2 by its position.
 */
export type FormContextDeviceSlots<TDevice> = {
  left?: TDevice;
  right?: TDevice;
  device1?: TDevice;
  device2?: TDevice;
};

export type RequestDeviceContext = {
  ear: string;
  name: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  warranty: string;
  warrantyExpires: string;
  // ENG-2494: only while the surface's flag is on.
  repairReasons?: string;
  modifications?: string;
  purchaseDate?: string;
  lossDamageWarrantyExpires?: string;
  extendedWarrantyExpires?: string;
  color?: string;
};

export type RequestContext = FormContextDeviceSlots<RequestDeviceContext> & {
  date: string;
  location: string;
  // ENG-2494: only while the surface's flag is on.
  contactFirstName?: string;
  contactLastName?: string;
  contactFullName?: string;
  contactEmail?: string;
  notes?: string;
  shippingMethod?: string;
  billingAccountNumber?: string;
  shippingAccountNumber?: string;
  orderId?: string;
  clinicOrderNumber?: string;
  supplierOrderNumber?: string;
  orderCreatedDate?: string;
  orderStatus?: string;
  vendorName?: string;
  vendorPhone?: string;
};

// ENG-2496: only while `digital-form-tags-loss-damage` is on.
export type LossDamageDeviceContext = {
  claimReason: string;
  service: string;
  servicePrice: string;
  deductibleOnRecord: string;
};

export type LossDamageContext =
  FormContextDeviceSlots<LossDamageDeviceContext> & {
    claimReason: string;
    claimDate?: string;
  };

export type OrderDeviceContext = {
  ear: string;
  name: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  color: string;
  battery: string;
  // ENG-2493: only while `digital-form-tags-order` is on.
  condition?: string;
  purchaseDate?: string;
};

export type OrderContext = FormContextDeviceSlots<OrderDeviceContext> & {
  date: string;
  location: string;
  // ENG-2493: only while `digital-form-tags-order` is on.
  billingAccountNumber?: string;
  shippingAccountNumber?: string;
  shippingMethod?: string;
  notes?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactFullName?: string;
  contactEmail?: string;
  clinicOrderNumber?: string;
  supplierOrderNumber?: string;
  createdDate?: string;
  status?: string;
  vendorName?: string;
  vendorPhone?: string;
};

export type LoanerDeviceContext = {
  manufacturer: string;
  model: string;
  serialNumber: string;
};

export type LoanerContext = FormContextDeviceSlots<LoanerDeviceContext> & {
  loanDate: string;
  dueDate: string;
};

// ENG-2495: the device that replaced each one going back, in its original's slot. Only while
// `digital-form-tags-exchange` is on.
export type ExchangeDeviceContext = {
  ear: string;
  name: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
};

export type ExchangeContext = FormContextDeviceSlots<ExchangeDeviceContext> & {
  exchangedDate: string;
  returnedToManufacturer: string;
};

/**
 * Lays a form's devices out in both slot layouts: Device1/Device2 by position, and
 * Left/Right by ear (the first device on each ear). A device with no ear fills only
 * its positional slot. Only the first two devices count — the forms carry two slots,
 * as the Anvil payloads do.
 */
export const buildDeviceSlots = <TDevice, TContext>(
  devices: readonly (TDevice | null | undefined)[],
  earOf: (device: TDevice) => string | null | undefined,
  toContext: (device: TDevice) => TContext
): FormContextDeviceSlots<TContext> => {
  const [device1, device2] = devices;
  const placed = [device1, device2].filter(
    (device): device is TDevice => device != null
  );
  const onEar = (ear: string) => placed.find((device) => earOf(device) === ear);

  const layout: [
    keyof FormContextDeviceSlots<TContext>,
    TDevice | null | undefined
  ][] = [
    ["left", onEar(LEFT_EAR_TYPE)],
    ["right", onEar(RIGHT_EAR_TYPE)],
    ["device1", device1],
    ["device2", device2]
  ];

  const slots: FormContextDeviceSlots<TContext> = {};
  layout.forEach(([slot, device]) => {
    if (device != null) slots[slot] = toContext(device);
  });
  return slots;
};

// Text only: a missing or non-text value prints blank, never "undefined" or "[object Object]".
const readText = (source: unknown, path: readonly string[]): string => {
  const value = path.reduce<unknown>(
    (node, key) =>
      node && typeof node === "object"
        ? (node as Record<string, unknown>)[key]
        : undefined,
    source
  );
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
};

export const FORM_CONTEXT_VALUES: MergeTagsReplaceHandler = Object.fromEntries(
  FORM_CONTEXT_LEAVES.map(({ value, path }) => [
    value,
    ({ extraData }) => readText(extraData, path)
  ])
);
